import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { ArticleCard, Loader, Overline, Breadcrumbs } from "@/components/shared";
import { AdSlot } from "@/components/Ads";
import { CONTENT_TYPES } from "@/lib/taxonomy";

const DESC = {
  AI: "Artificial intelligence news, analysis and explainers — models, agents, tools and the companies shaping them.",
  Gaming: "Games, consoles, handhelds and hardware — reviews, news and deep dives across the gaming world.",
  Tech: "Smartphones, laptops, hardware and the technology that matters, reviewed and explained.",
  Reviews: "Hands-on verdicts on the AI tools, devices and products worth your attention.",
  Guides: "Practical how-to guides to get more out of AI and technology.",
};
const TYPES = ["All", ...CONTENT_TYPES];

export default function Category({ cat }) {
  const [items, setItems] = useState(null);
  const [type, setType] = useState("All");
  const [q, setQ] = useState("");
  useSEO({ title: `${cat} — News, Reviews & Analysis`, description: DESC[cat] });
  useEffect(() => {
    setItems(null);
    const t = setTimeout(() => api.articles({ category: cat, content_type: type, search: q }).then((d) => setItems(d.items)), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [cat, type, q]);

  return (
    <div className="klyroo-radial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: cat }]} />
        <Overline>Coverage</Overline>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">{cat}</h1>
        <p className="text-slate-400 mt-3 max-w-2xl text-lg">{DESC[cat]}</p>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-8 mb-6">
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`type-${t}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${type === t ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>{t}</button>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="category-search"
            className="bg-klyroo-surface border border-white/10 rounded-xl px-4 py-2 text-sm text-white outline-none focus:border-klyroo-cyan/50" />
        </div>

        {!items ? <Loader /> : items.length === 0 ? (
          <p className="text-slate-500 py-20 text-center font-mono">No articles found.</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-grid">
            {items.flatMap((a, i) => {
              const cells = [<ArticleCard key={a.slug} a={a} />];
              if ((i + 1) % 7 === 0 && i !== items.length - 1) {
                cells.push(<AdSlot key={`feed-ad-${i}`} placement="feed-square" className="h-full" />);
              }
              return cells;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
